import React, { useState, useEffect } from 'react';
import { useModalAnimation } from '../../hooks/useModalAnimation';
import { X, Search, Plus } from 'lucide-react';
import { Input } from '../ui/Input';
import { GhostButton } from '../ui/GhostButton';
import { PrimaryButton } from '../ui/PrimaryButton';
import { AddressFormModal } from './AddressFormModal';
import { useSortableData } from '../../hooks/useSortableData';
import { SortableHeader } from '../ui/SortableHeader';

export const AddressAssignmentModal = ({ isOpen, onClose, productSku, onUpdate }) => {
    const { isVisible, isClosing, handleAnimationEnd } = useModalAnimation(isOpen, onClose);
    const [addresses, setAddresses] = useState([]);
    const [assignedAddresses, setAssignedAddresses] = useState([]);
    const [search, setSearch] = useState('');
    const [isFormOpen, setIsFormOpen] = useState(false);

    // --- SORTING ---
    const { items: sortedAddresses, requestSort, sortConfig } = useSortableData(addresses);

    // ... (rest of logic)

    const fetchAddresses = async () => {
        try {
            const queryParams = new URLSearchParams();
            if (search) queryParams.append('search', search);

            const res = await fetch(`/api/master/addresses?${queryParams.toString()}`, {
                headers: { 'Authorization': `Bearer ${localStorage.getItem('token')}` }
            });
            if (res.ok) {
                const data = await res.json();
                setAddresses(data);
            }
        } catch (err) {
            console.error("Error fetching master addresses:", err);
        }
    };

    const fetchAssignedAddresses = async () => {
        if (!productSku) return;
        try {
            const res = await fetch(`/api/products/${productSku}/addresses`, {
                headers: { 'Authorization': `Bearer ${localStorage.getItem('token')}` }
            });
            if (res.ok) {
                const data = await res.json();
                setAssignedAddresses(data.map(a => a.id));
            }
        } catch (err) {
            console.error("Error fetching assigned addresses:", err);
        }
    };

    useEffect(() => {
        if (isOpen && productSku) {
            fetchAddresses();
            fetchAssignedAddresses();
        }
    }, [isOpen, productSku, search]);

    const handleToggle = async (addressId) => {
        const isAssigned = assignedAddresses.includes(addressId);
        try {
            let res;
            if (isAssigned) {
                // Unassign
                res = await fetch(`/api/products/${productSku}/addresses/${addressId}`, {
                    method: 'DELETE',
                    headers: { 'Authorization': `Bearer ${localStorage.getItem('token')}` }
                });
            } else {
                // Assign
                res = await fetch(`/api/products/${productSku}/addresses`, {
                    method: 'POST',
                    headers: {
                        'Content-Type': 'application/json',
                        'Authorization': `Bearer ${localStorage.getItem('token')}`
                    },
                    body: JSON.stringify({ address_id: addressId, type: 'Delivery' }) // Default type
                });
            }

            if (res.ok) {
                fetchAssignedAddresses();
                if (onUpdate) onUpdate();
            }
        } catch (err) {
            console.error("Error toggling address assignment:", err);
        }
    };

    const handleSaveForm = async (formData) => {
        try {
            const res = await fetch('/api/master/addresses', {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': `Bearer ${localStorage.getItem('token')}`
                },
                body: JSON.stringify(formData)
            });

            if (res.ok) {
                setIsFormOpen(false);
                fetchAddresses();
                // Optionally auto-assign the new address?
                // For now just refresh list.
            }
        } catch (err) {
            console.error("Error saving new address:", err);
        }
    };

    if (!isVisible) return null;

    return (
        <div
            className={`fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-50 ${isClosing ? 'animate-out fade-out duration-200' : 'animate-in fade-in duration-200'}`}
            onAnimationEnd={handleAnimationEnd}
        >
            <div className={`bg-stone-900 border border-stone-800 rounded-xl shadow-2xl w-[900px] max-h-[90vh] flex flex-col ${isClosing ? 'animate-out zoom-out-95 duration-200' : 'animate-in zoom-in-95 duration-200'}`}>
                <div className="flex justify-between items-center p-6 border-b border-stone-800">
                    <h2 className="text-xl font-bold text-white">Add Address to Product</h2>
                    <button onClick={onClose} className="text-stone-400 hover:text-white transition">
                        <X size={20} />
                    </button>
                </div>

                <div className="p-4 border-b border-stone-800 flex gap-4">
                    <div className="relative flex-1">
                        <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-stone-500" size={16} />
                        <Input
                            placeholder="Search contacts..."
                            value={search}
                            onChange={(e) => setSearch(e.target.value)}
                            className="pl-10 bg-stone-950 border-stone-700 w-full"
                        />
                    </div>
                    <GhostButton onClick={() => setIsFormOpen(true)}>
                        <Plus size={16} /> Create New Contact
                    </GhostButton>
                </div>

                <div className="flex-1 overflow-y-auto p-4">
                    <table className="w-full text-left border-collapse">
                        <thead className="bg-stone-950 text-stone-500 text-xs font-bold uppercase sticky top-0">
                            <tr>
                                <th className="p-3 w-10"></th>
                                <SortableHeader label="Company" sortKey="company_name" currentSort={sortConfig} onSort={requestSort} />
                                <SortableHeader label="Contact" sortKey="last_name" currentSort={sortConfig} onSort={requestSort} />
                                <SortableHeader label="City/Country" sortKey="city" currentSort={sortConfig} onSort={requestSort} />
                                <SortableHeader label="Type" sortKey="type" currentSort={sortConfig} onSort={requestSort} />
                            </tr>
                        </thead>
                        <tbody className="divide-y divide-stone-800 text-sm text-stone-300">
                            {sortedAddresses.map(addr => (
                                <tr
                                    key={addr.id}
                                    className="hover:bg-stone-800/50 transition-colors cursor-pointer"
                                    onClick={() => handleToggle(addr.id)}
                                >
                                    <td className="p-3">
                                        <input
                                            type="checkbox"
                                            checked={assignedAddresses.includes(addr.id)}
                                            readOnly
                                            className="rounded border-stone-700 bg-stone-900 text-blue-600 focus:ring-0 focus:ring-offset-0 pointer-events-none"
                                        />
                                    </td>
                                    <td className="p-3 font-medium text-white">{addr.company_name}</td>
                                    <td className="p-3">{addr.first_name} {addr.last_name}</td>
                                    <td className="p-3">{addr.city}, {addr.country}</td>
                                    <td className="p-3">
                                        <span className={`px-2 py-1 rounded text-xs font-medium border ${addr.type === 'Main' ? 'bg-purple-900/20 border-purple-800 text-white' :
                                            addr.type === 'Supplier' ? 'bg-blue-900/20 border-blue-800 text-white' :
                                                'bg-stone-800 border-stone-700 text-white'
                                            }`}>
                                            {addr.type}
                                        </span>
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>

                <div className="p-4 border-t border-stone-800 flex justify-end">
                    <PrimaryButton onClick={onClose}>Done</PrimaryButton>
                </div>
            </div>

            <AddressFormModal
                isOpen={isFormOpen}
                onClose={() => setIsFormOpen(false)}
                onSave={handleSaveForm}
            />
        </div>
    );
};
